import React, { useState, useRef } from 'react';

function InventoryImportModal({ onImport, onClose }) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const fileInputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelection(files[0]);
    }
  };

  const handleFileSelection = (file) => {
    if (!file.name.toLowerCase().endsWith('.json')) {
      alert('Please select a JSON file.');
      return;
    }

    setSelectedFile(file);
    
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const content = JSON.parse(e.target.result);
        
        // Validate file format
        if (content.type !== 'inventory_full_export') {
          alert('Invalid file format. Please select a valid inventory export file.');
          setSelectedFile(null);
          return;
        }
        
        setFileContent(content);
        setPreviewData({
          inventoryName: content.inventory?.name || 'Unknown',
          itemsCount: content.items_count || 0,
          inventoryType: content.inventory_type?.name || 'None',
          locationType: content.location_type?.name || 'None',
          exportedAt: content.exported_at
        });
      } catch (error) {
        alert('Error reading file. Please ensure it\'s a valid JSON file.');
        setSelectedFile(null);
      }
    };
    
    reader.readAsText(file);
  };

  const handleFileInput = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelection(file);
    }
  };

  const handleImport = async () => {
    if (!fileContent) {
      alert('Please select a file first.');
      return;
    }

    setIsProcessing(true);
    try {
      await onImport(fileContent);
      onClose();
    } catch (error) {
      // Error handling is done in the parent component
      console.error('Import error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const resetFile = () => {
    setSelectedFile(null);
    setFileContent(null);
    setPreviewData(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="modal">
      <div className="modal-content" style={{ maxWidth: '600px' }}>
        <h2>Import Inventory</h2>
        
        {!selectedFile ? (
          <div>
            <div
              className={`file-drop-zone ${dragActive ? 'active' : ''}`}
              style={{
                border: '2px dashed #ddd',
                borderRadius: '8px',
                padding: '2rem',
                textAlign: 'center',
                backgroundColor: dragActive ? '#f0f8ff' : '#fafafa',
                borderColor: dragActive ? '#3498db' : '#ddd',
                cursor: 'pointer',
                marginBottom: '1rem'
              }}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>📁</div>
              <h3>Drop your inventory export file here</h3>
              <p style={{ color: '#666' }}>or click to browse files</p>
              <p style={{ fontSize: '0.8rem', color: '#999', marginTop: '1rem' }}>
                Only JSON files exported from this application are supported
              </p>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileInput}
              style={{ display: 'none' }}
            />
          </div>
        ) : (
          <div>
            <div style={{ 
              backgroundColor: '#e8f5e8', 
              padding: '1rem', 
              borderRadius: '4px',
              border: '1px solid #c3e6c3',
              marginBottom: '1rem'
            }}>
              <h3 style={{ margin: '0 0 0.5rem 0', color: '#2d5a2d' }}>
                ✅ File loaded successfully
              </h3>
              <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                <strong>File:</strong> {selectedFile.name}
              </p>
              {previewData && (
                <div>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                    <strong>Inventory:</strong> {previewData.inventoryName}
                  </p>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                    <strong>Items:</strong> {previewData.itemsCount}
                  </p>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                    <strong>Inventory Type:</strong> {previewData.inventoryType}
                  </p>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                    <strong>Location Type:</strong> {previewData.locationType}
                  </p>
                  <p style={{ margin: '0.25rem 0', fontSize: '0.9rem' }}>
                    <strong>Exported:</strong> {previewData.exportedAt}
                  </p>
                </div>
              )}
            </div>

            <div style={{ 
              backgroundColor: '#fff3cd', 
              padding: '1rem', 
              borderRadius: '4px',
              border: '1px solid #ffeaa7',
              marginBottom: '1rem'
            }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#856404' }}>
                ⚠️ Import Warning
              </h4>
              <ul style={{ margin: '0.5rem 0', paddingLeft: '1.5rem', fontSize: '0.9rem' }}>
                <li>All current items in this inventory will be <strong>permanently deleted</strong></li>
                <li>The inventory name and description may be updated</li>
                <li>The inventory type and location type will be updated if different</li>
                <li>Missing inventory types and location types will be automatically created</li>
                <li>This action cannot be undone</li>
              </ul>
            </div>

            <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={resetFile}
                style={{ flex: 1 }}
              >
                Choose Different File
              </button>
            </div>
          </div>
        )}

        <div style={{ 
          display: 'flex', 
          justifyContent: 'flex-end', 
          gap: '1rem', 
          marginTop: '2rem',
          paddingTop: '1rem',
          borderTop: '1px solid #ddd'
        }}>
          <button 
            type="button" 
            className="btn" 
            onClick={onClose}
            disabled={isProcessing}
          >
            Cancel
          </button>
          <button 
            type="button" 
            className="btn" 
            onClick={handleImport}
            disabled={!fileContent || isProcessing}
            style={{ 
              backgroundColor: fileContent ? '#e67e22' : '#ccc',
              color: 'white'
            }}
          >
            {isProcessing ? 'Importing...' : 'Import Inventory'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default InventoryImportModal;